import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createSkillCategory,
  deleteSkillCategory,
  getSkillCategories,
  updateSkillCategory,
} from '../services/skill-category.service';
import {
  GetSkillCategoriesParams,
  SkillCategoryInsertInput,
  SkillCategoryUpdateInput,
} from '../types/skill-category.types';

const invalidateSkillCategoryQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-skill-categories'] });

export const useGetSkillCategories = (params: GetSkillCategoriesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-skill-categories', params],
    queryFn: () => getSkillCategories(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSkillCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: SkillCategoryInsertInput) => createSkillCategory(input),
    onSuccess: () => {
      invalidateSkillCategoryQueries(queryClient);
      toast({ title: t('SKILL_CATEGORY_CREATED') });
    },
  });
};

export const useUpdateSkillCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: SkillCategoryUpdateInput }) =>
      updateSkillCategory(params),
    onSuccess: () => {
      invalidateSkillCategoryQueries(queryClient);
      toast({ title: t('SKILL_CATEGORY_UPDATED') });
    },
  });
};

export const useDeleteSkillCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteSkillCategory(itemId),
    onSuccess: () => {
      invalidateSkillCategoryQueries(queryClient);
      toast({ title: t('SKILL_CATEGORY_DELETED') });
    },
  });
};
